import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useModal } from '@context/ModalsContext'

import { AlertIconWrapper } from '@ui/AlertIconWrapper/AlertIconWrapper'
import { Button, IconButton } from '@ui/components/Button'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { KebabIcon } from '@ui/icons/Kebab.icon'
import { WarningIcon } from '@ui/icons/Warning.icon'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/v2/Table/Table'

import { CommonPaths } from '@utils/path'
import { WalletRestrictions, isRestricted, isWalletSuspended } from '@utils/wallet.utils'

import { WalletDto } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useFundingNav } from '@hooks/useFundingNav'

import { Box } from '@shared/components/Box'
import { Dropdown } from '@shared/components/Dropdown/Dropdown'
import { DropdownCard } from '@shared/components/Dropdown/DropdownCard/DropdownCard'
import { DropdownList } from '@shared/components/Dropdown/DropdownList/DropdownList'
import { SupportModal } from '@shared/modals/Support.modal'

import { useDepositTracker } from '@analytics/amplitude/deposit'

import { WalletBalancesOptionsContent } from './WalletBalancesOptions'

type ExtendedWalletDto = WalletDto & { isOpen?: boolean }

interface WalletTableProps {
  wallets: ExtendedWalletDto[]
  onWithdrawal: (walletId: string) => void
  isLoading: boolean
}

export const WalletBalancesTable: React.FC<WalletTableProps> = (props) => {
  const { wallets, onWithdrawal, isLoading } = props
  const tracker = useDepositTracker()

  const { t } = useTranslation()
  const [openId, setOpenId] = useState<string | null>(null)
  const restrictionModal = useModal(SupportModal, {
    title: t('Temporarily unavailable'),
    description: t('Please contact Support for assistance.'),
  })

  const { formatMoney } = useFormatNumber()
  const navigate = useNavigate()
  const { navigateToFunding } = useFundingNav()

  return (
    <Table isLoading={isLoading} noResults={!wallets.length}>
      <TableHeader>
        <TableRow>
          <TableHead>{t('Wallet.Wallet')}</TableHead>
          <TableHead>{t('Wallet.Available Balance')}</TableHead>
          <TableHead>{t('Wallet.Reserved')}</TableHead>
          <TableHead />
          <TableHead />
          <TableHead />
        </TableRow>
      </TableHeader>

      <TableBody>
        {wallets.map((wallet) => {
          const hasAllRestrictions = isWalletSuspended(wallet.restrictions)
          const isDepositRestricted = isRestricted(WalletRestrictions.DEPOSIT, wallet.restrictions)
          const isTransferFromRestricted = isRestricted(
            WalletRestrictions.TRANSFER_FROM,
            wallet.restrictions
          )

          const isDropdownOpen = openId === wallet.id

          return (
            <TableRow key={wallet.id}>
              <TableCell>
                <Box flex align='center' gap='x8'>
                  {hasAllRestrictions && (
                    <IconButton.Ghost
                      size='md'
                      icon={WarningIcon}
                      onClick={() => restrictionModal.open()}
                    />
                  )}
                  {wallet.currency.id} {t('Wallet.Wallet')}
                </Box>
              </TableCell>

              <TableCell>
                <strong>{formatMoney(wallet.balance, wallet.currency.id)}</strong>
              </TableCell>

              <TableCell>{formatMoney(wallet.reserved, wallet.currency.id)}</TableCell>

              <TableCell hasTruncate={false}>
                <AlertIconWrapper spaced isActive={!hasAllRestrictions && isDepositRestricted}>
                  <Tooltip
                    content={t('This action is currently unavailable.')}
                    disabled={!isDepositRestricted}
                  >
                    <Button.Primary
                      size='sm'
                      disabled={isDepositRestricted}
                      onClick={() => {
                        tracker.start({
                          entry_point: 'balances',
                          preset_wallet_id: wallet.id,
                        })
                        navigateToFunding(`${CommonPaths.TradersRoomWallets}/${wallet.id}/deposit`)
                      }}
                    >
                      {t('Deposit')}
                    </Button.Primary>
                  </Tooltip>
                </AlertIconWrapper>
              </TableCell>

              <TableCell hasTruncate={false}>
                <AlertIconWrapper spaced isActive={!hasAllRestrictions && isTransferFromRestricted}>
                  <Tooltip
                    content={t('This action is currently unavailable.')}
                    disabled={!isTransferFromRestricted}
                  >
                    <Button.Secondary
                      size='sm'
                      disabled={isTransferFromRestricted}
                      onClick={() =>
                        navigate(`${CommonPaths.TradersRoomWallets}/${wallet.id}/transfer`)
                      }
                    >
                      {t('Transfer')}
                    </Button.Secondary>
                  </Tooltip>
                </AlertIconWrapper>
              </TableCell>

              <TableCell hasTruncate={false} align='right' width={50}>
                <Dropdown
                  direction='topRight'
                  distance='0.5rem'
                  isOpen={isDropdownOpen}
                  setIsOpen={(open) => setOpenId(open ? wallet.id : null)}
                  content={
                    <DropdownCard>
                      <DropdownList>
                        <WalletBalancesOptionsContent
                          wallet={wallet}
                          isLoading={isLoading}
                          onWithdrawal={onWithdrawal}
                          close={() => setOpenId(null)}
                        />
                      </DropdownList>
                    </DropdownCard>
                  }
                >
                  <IconButton.Ghost
                    size='md'
                    icon={KebabIcon}
                    onClick={() => setOpenId(wallet.id)}
                  />
                </Dropdown>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
